import React from "react";

import ActionsDropdown from "../../../../../components/Dropdown/ActionsDropdown";
import { ValuationBadge } from "../../../../../components/Valuation";
import { CONTEXT_RAIL_PANEL_ID } from "../../constants";
import { ButtonTypes } from "../../../../../components/Button";
import { INSPECT_POSITION_HINT } from "./constants";
import { buildHoldingDisplay, priceOverrideEntryFor } from "./holdingDisplay";
import { selectOnBareClick } from "./inspectClick";
import { HoldingItemProps } from "./types";
import {
  AllocationNote,
  Cell,
  CostText,
  InspectHint,
  Instrument,
  InstrumentMark,
  KebabAlign,
  InstrumentName,
  InstrumentSymbol,
  NameStack,
  PriceAmount,
  PriceStack,
  PriceUpdatedNote,
  QuantityText,
  ReturnAmount,
  ReturnPercent,
  ReturnStack,
  InspectPositionButton,
  Row,
  ValuationNote,
  ValueAmount,
  ValueStack,
  ValueRow,
  PriceOverrideButton,
} from "./styles";

export const HoldingRow: React.FC<HoldingItemProps> = ({
  holding,
  actions,
  onSetPrice,
  isSelected,
  onToggleSelect,
}) => {
  const {
    valueText,
    badge,
    valuationMessage,
    priceText,
    priceUpdated,
    instrumentSubtitle,
    quantityText,
    costBasisText,
    allocationText,
    markText,
    markColor,
    returnAmountText,
    returnPercentText,
    returnTone,
  } = buildHoldingDisplay(holding);

  // `ActionsDropdown` filters on `show` itself, so gate on the same criterion
  // the mobile card uses — otherwise a hidden action would leave an empty menu.
  const hasActions = actions.some((action) => action.show);

  const priceOverrideEntry = priceOverrideEntryFor(holding, onSetPrice);

  return (
    <Row
      $selected={isSelected}
      onClick={selectOnBareClick(() => onToggleSelect(holding))}
    >
      <Cell>
        <InspectPositionButton
          type="button"
          aria-pressed={isSelected}
          aria-controls={CONTEXT_RAIL_PANEL_ID}
          aria-label={`Inspect ${holding.instrument.symbol} position`}
          onClick={() => onToggleSelect(holding)}
        >
          <Instrument>
            <InstrumentMark $color={markColor} aria-hidden="true">
              {markText}
            </InstrumentMark>
            <NameStack>
              <InstrumentSymbol>{holding.instrument.symbol}</InstrumentSymbol>
              <InstrumentName>{instrumentSubtitle}</InstrumentName>
              <InspectHint>{INSPECT_POSITION_HINT}</InspectHint>
            </NameStack>
          </Instrument>
        </InspectPositionButton>
      </Cell>
      <Cell>
        <QuantityText>{quantityText}</QuantityText>
      </Cell>
      <Cell data-testid={`holding-price-${holding.id}`}>
        <PriceStack>
          <PriceAmount>{priceText}</PriceAmount>
          <PriceUpdatedNote priceUpdated={priceUpdated} />
        </PriceStack>
      </Cell>
      <Cell data-testid={`holding-cost-${holding.id}`}>
        <CostText>{costBasisText}</CostText>
      </Cell>
      <Cell>
        <ValueStack>
          <ValueRow>
            <ValueAmount>{valueText}</ValueAmount>
            {badge && <ValuationBadge badge={badge} hasVisibleNote={true} />}
          </ValueRow>
          {allocationText && <AllocationNote>{allocationText}</AllocationNote>}
          {valuationMessage && (
            <ValuationNote>{valuationMessage}</ValuationNote>
          )}
          {priceOverrideEntry && (
            <PriceOverrideButton
              type={ButtonTypes.link}
              size="small"
              onClick={priceOverrideEntry.open}
            >
              {priceOverrideEntry.label}
            </PriceOverrideButton>
          )}
        </ValueStack>
      </Cell>
      <Cell>
        <ReturnStack>
          <ReturnAmount $tone={returnTone}>{returnAmountText}</ReturnAmount>
          {returnPercentText && (
            <ReturnPercent>{returnPercentText}</ReturnPercent>
          )}
        </ReturnStack>
      </Cell>
      <Cell>
        <KebabAlign>
          {hasActions && (
            <ActionsDropdown
              compact
              dropdownActions={actions}
              testId={`holding-actions-${holding.id}`}
            />
          )}
        </KebabAlign>
      </Cell>
    </Row>
  );
};
